import publicUrls from "../../routes/publicUrls";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import { linksData as originalLinks } from "../../Model/Navbar";
import { auctionsLive } from "../../view/Model/Auction";
import LogoImg from "../../images/resources/dddd.png";
import { tokens } from "../../theme/galleryTheme";



const SECONDARY_LINKS = [
  { label: "About us", path: "/about" },
  { label: "Contact us", path: publicUrls.contact() },
  { label: "How to buy", path: "/how-to-buy" },
  { label: "How to sell", path: "/how-to-sell" },
  { label: "FAQ", path: "/faq" },
];

const subHref = (sub) => `${sub.to}?data=${sub.name}`;

function LiveDot() {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", bgcolor: tokens.color.live, mr: 1, flexShrink: 0 }}
    />
  );
}

export default function Menu({ isMobile, ClickLogOut }) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [search, setSearch] = useState("");
  const [token] = useState(localStorage.getItem("token") || "");
  const [isLiveOpen, setIsLiveOpen] = useState(false);
  const navRef = useRef(null);

  // Same live check as before.
  useEffect(() => {
    const checkLiveStatus = async () => {
      try {
        const results = await auctionsLive();
        if (results && results.success && results.auction_details) {
          // eslint-disable-next-line eqeqeq
          setIsLiveOpen(results.auction_details.isLive == 1);
        } else {
          setIsLiveOpen(false);
        }
      } catch (error) {
        console.error("LiveAuction Error:", error);
        setIsLiveOpen(false);
      }
    };
    checkLiveStatus();
  }, []);

  const liveLinkItem = originalLinks.find((link) => link.name === "LIVE AUCTION");
  const filteredLinks = originalLinks.filter((link) => link.name !== "LIVE AUCTION");
  const finalLinks = isLiveOpen && liveLinkItem ? [{ ...liveLinkItem, isLive: true }, ...filteredLinks] : filteredLinks;

  // Close an open desktop dropdown on Escape or on a click outside the menu.
  useEffect(() => {
    if (openDropdown === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [openDropdown]);

  /* ------------------------------ Mobile ------------------------------ */
  if (isMobile) {
    const handleMobileSearch = (e) => {
      e.preventDefault();
      if (search.trim()) window.location.href = `/search/${search.trim()}`;
    };

    const rowSx = { minHeight: 52, px: 2.5 };
    const rowTextSx = {
      fontSize: "0.9375rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: tokens.color.ink,
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
    };

    return (
      <>
        <IconButton
          onClick={() => setOpenDrawer(true)}
          aria-label="Open menu"
          aria-haspopup="dialog"
          aria-expanded={openDrawer}
          sx={{ color: tokens.color.ink, width: 48, height: 48 }}
        >
          <MenuIcon fontSize="large" />
        </IconButton>

        <Drawer
          id="site-mobile-menu"
          anchor="left"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          PaperProps={{
            className: "lg-gallery",
            role: "dialog",
            "aria-modal": true,
            "aria-label": "Site menu",
            sx: { width: "min(88vw, 360px)", display: "flex", flexDirection: "column" },
          }}
        >
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <Box sx={{ px: 2.5, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box component="a" href="/home" aria-label="León Gallery — home" sx={{ display: "flex" }}>
                <img src={LogoImg} alt="León Gallery" style={{ height: 36, width: "auto", display: "block" }} />
              </Box>
              <IconButton onClick={() => setOpenDrawer(false)} aria-label="Close menu" sx={{ width: 48, height: 48 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box component="form" role="search" onSubmit={handleMobileSearch} sx={{ px: 2.5, pb: 2 }}>
              <label htmlFor="mobile-site-search" className="lg-sr-only">
                Search artists, works or auctions
              </label>
              <Box sx={{ display: "flex", alignItems: "center", border: `1px solid ${tokens.color.line}`, pl: 1.5 }}>
                <InputBase
                  id="mobile-site-search"
                  placeholder="Search artists, works, auctions"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ type: "search" }}
                  sx={{
                    flex: 1,
                    fontSize: "1rem",
                    "& input::placeholder": { color: tokens.color.muted, opacity: 1 },
                  }}
                />
                <IconButton type="submit" aria-label="Search" sx={{ width: 48, height: 48, color: tokens.color.ink }}>
                  <SearchIcon />
                </IconButton>
              </Box>
            </Box>

            <Divider />

            <Box component="nav" aria-label="Main">
              <List disablePadding>
                {finalLinks.map((val) => {
                  const hasDropdown = Boolean(val.dropdown);
                  const expanded = openMobileSection === val.name;
                  const sectionId = `mobile-sub-${val.name.replace(/[^A-Za-z0-9]/g, "")}`;

                  return (
                    <Box component="li" key={val.name} sx={{ listStyle: "none" }}>
                      {hasDropdown ? (
                        <ListItemButton
                          onClick={() => setOpenMobileSection(expanded ? null : val.name)}
                          aria-expanded={expanded}
                          aria-controls={sectionId}
                          sx={rowSx}
                        >
                          <Typography component="span" sx={rowTextSx}>
                            {val.name}
                          </Typography>
                          {expanded ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      ) : (
                        <ListItemButton
                          component="a"
                          href={val.to}
                          sx={{ ...rowSx, bgcolor: val.isLive ? "#FBEFEF" : "transparent" }}
                        >
                          <Typography component="span" sx={{ ...rowTextSx, color: val.isLive ? tokens.color.live : tokens.color.ink }}>
                            {val.isLive && <LiveDot />}
                            {val.name}
                          </Typography>
                        </ListItemButton>
                      )}

                      {hasDropdown && (
                        <Collapse in={expanded} id={sectionId}>
                          <List disablePadding sx={{ bgcolor: tokens.color.ivory }}>
                            {val.dropdown.map((sub) => (
                              <Box component="li" key={sub.name} sx={{ listStyle: "none" }}>
                                <ListItemButton component="a" href={subHref(sub)} sx={{ minHeight: 48, pl: 4.5 }}>
                                  <Typography component="span" sx={{ fontSize: "0.9375rem", color: tokens.color.text }}>
                                    {sub.name}
                                  </Typography>
                                </ListItemButton>
                              </Box>
                            ))}
                          </List>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Box>

            <Divider />

            <Box component="nav" aria-label="Information" sx={{ px: 2.5, py: 1.5 }}>
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {SECONDARY_LINKS.map((item) => (
                  <li key={item.path}>
                    <Box
                      component="a"
                      href={item.path}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 44,
                        color: tokens.color.secondary,
                        fontSize: "0.9375rem",
                        textDecoration: "none",
                        "&:hover": { color: tokens.color.ink, textDecoration: "underline" },
                      }}
                    >
                      {item.label}
                    </Box>
                  </li>
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: 2, borderTop: `1px solid ${tokens.color.line}`, display: "flex", gap: 1.5 }}>
            {token ? (
              <>
                <Button variant="contained" href="/my/account" fullWidth>
                  My account
                </Button>
                <Button variant="outlined" onClick={ClickLogOut} fullWidth>
                  Log out
                </Button>
              </>
            ) : (
              <Button variant="contained" href="/login" fullWidth>
                Sign in
              </Button>
            )}
          </Box>
        </Drawer>
      </>
    );
  }

  /* ------------------------------ Desktop ----------------------------- */
  const topItemSx = (active, isLive) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    minHeight: 44,
    px: 0.5,
    background: "none",
    border: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: { lg: "0.8125rem", xl: "0.875rem" }, // 13px on 1200–1535px screens, 14px above
    fontWeight: 600,
    letterSpacing: { lg: "0.05em", xl: "0.08em" },
    textTransform: "uppercase",
    textDecoration: "none",
    whiteSpace: "nowrap",
    color: isLive ? tokens.color.live : tokens.color.ink,
    borderBottom: `2px solid ${active ? tokens.color.ink : "transparent"}`,
    "&:hover": { borderBottomColor: isLive ? tokens.color.live : tokens.color.ink },
  });

  return (
    <Box component="nav" aria-label="Main" ref={navRef}>
      <Box component="ul" sx={{ display: "flex", listStyle: "none", gap: { lg: 2, xl: 4 }, m: 0, p: 0, alignItems: "center" }}>
        {finalLinks.map((val, i) => {
          const hasDropdown = Boolean(val.dropdown);
          const open = openDropdown === i;
          const panelId = `nav-panel-${i}`;

          return (
            <Box
              component="li"
              key={val.name}
              onMouseLeave={() => setOpenDropdown(null)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setOpenDropdown(null);
              }}
              sx={{ position: "relative" }}
            >
              {hasDropdown ? (
                <Box
                  component="button"
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => setOpenDropdown(open ? null : i)}
                  sx={topItemSx(open, false)}
                >
                  {val.name}
                  <ExpandMore fontSize="small" sx={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }} />
                </Box>
              ) : (
                <Box component="a" href={val.to} sx={topItemSx(false, val.isLive)}>
                  {val.isLive && <LiveDot />}
                  {val.name}
                </Box>
              )}

              {hasDropdown && (
                <Box
                  id={panelId}
                  sx={{
                    display: open ? "block" : "none",
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    pt: 1.5, // hover bridge so the panel doesn't close when moving the pointer down
                    zIndex: 1000,
                  }}
                >
                  <Box
                    component="ul"
                    sx={{
                      listStyle: "none",
                      m: 0,
                      py: 1,
                      px: 0,
                      minWidth: 240,
                      bgcolor: "#fff",
                      border: `1px solid ${tokens.color.line}`,
                      boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
                    }}
                  >
                    {val.dropdown.map((sub) => (
                      <li key={sub.name}>
                        <Box
                          component="a"
                          href={subHref(sub)}
                          onClick={() => setOpenDropdown(null)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minHeight: 44,
                            px: 3,
                            color: tokens.color.text,
                            fontSize: "0.9375rem",
                            textDecoration: "none",
                            "&:hover, &:focus-visible": { bgcolor: tokens.color.ivory, color: tokens.color.ink },
                          }}
                        >
                          {sub.name}
                        </Box>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
